/**
 * DirectoryListSkeleton Component
 *
 * Content-only loading skeleton for directory list views.
 * Used for tab-specific loading states to avoid re-rendering full page skeleton.
 *
 * IMPORTANT (per FRONTEND_CODE_REVIEW_LEARNINGS):
 * - NEVER add animate-pulse to parent div containers
 * - The Skeleton component from shadcn/ui already includes animation
 * - Adding animate-pulse to parents causes double animation and flickering
 */

import type React from "react";
import { Skeleton } from "@planta-v2/pds-ui/skeleton";
import { cn } from "@planta-v2/pds-ui/lib/utils";

type SkeletonVariant = "row" | "card";

interface DirectoryListSkeletonProps {
  /**
   * "row" mirrors ProfileListItem — the All, Top Picks and Connected tabs.
   * "card" mirrors ConnectionRequestCard and SentRequestCard, which are a
   * bordered shell with a message body, not a 64px row.
   */
  readonly variant?: SkeletonVariant;
  /**
   * Cards only. ConnectionRequestCard carries an accept pill and an ignore
   * link; SentRequestCard carries neither. The two differ by 56px, so the
   * placeholder has to be told which one it stands in for.
   */
  readonly showActions?: boolean;
  readonly rowCount?: number | undefined;
  /** Mirrors ProfileListItem's isCompact, so a search placeholder is as tall as the row that replaces it. */
  readonly isCompact?: boolean;
  /** Compact only: mirrors ProfileListItem's isBioShown, one line taller. */
  readonly hasBioLine?: boolean;
  readonly className?: string | undefined;
}

export function DirectoryListSkeleton({
  variant = "row",
  showActions = false,
  rowCount = 6,
  isCompact = false,
  hasBioLine = false,
  className,
}: DirectoryListSkeletonProps = {}): React.JSX.Element {
  if (variant === "card") {
    return (
      <div className={cn("flex flex-col", className)}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-foreground/15 bg-surface p-4 mb-3"
          >
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full shrink-0" />
              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <Skeleton className="mt-3 h-5 w-full" />
            {showActions && (
              <div className="mt-4 flex items-center gap-3">
                <Skeleton className="h-10 flex-1 rounded-full" />
                <Skeleton className="h-4 w-12" />
              </div>
            )}
            <Skeleton className="mt-3 h-5 w-20" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("divide-y divide-foreground/10", className)}>
      {Array.from({ length: rowCount }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "flex items-center gap-3",
            isCompact
              ? hasBioLine
                ? "py-2.5 h-[73px]"
                : "py-2.5 h-[60px]"
              : "py-3 h-16",
          )}
        >
          <Skeleton className="h-10 w-10 rounded-full shrink-0" />
          <div className="flex flex-col gap-1 flex-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
            {hasBioLine && <Skeleton className="h-3 w-40" />}
          </div>
        </div>
      ))}
    </div>
  );
}
